/**
 * Per-question-type renderers for the adaptive flow.
 *
 * Mirrors the legacy Module/index.tsx render branches for question
 * types 4 (HotText) and 7 (FillInTheBlank). The legacy version reads
 * the helper text from ``questionHelper.questionHelper`` and replaces
 * ``[[markers]]`` with interactive spans/inputs; this file does the
 * same so the visual treatment matches without a Phase-E refactor.
 *
 * MC (type 1) renders are inline in AdaptiveModule.tsx via ``McChoices``.
 */

import React, { useEffect, useMemo, useRef } from 'react';
import renderMathInElement from 'katex/dist/contrib/auto-render.js';
import { setupShsatMathKeyboard } from '../shsatMathKeyboard';
import { validateMathEntry } from '../mathFillIn';
import { escapeHtmlAttr } from 'global/escapeHtml';
import { plainizeMath } from 'global/plainizeMath';


export interface QuestionHelperPayload {
  id: number;
  questionHelper: string;
}


// ---------------------------------------------------------------------------
// HotText (type 4)
// ---------------------------------------------------------------------------


export function HotTextHelper(props: {
  helper: QuestionHelperPayload | null | undefined;
  selectedIndices: number[];
  onChange: (next: number[]) => void;
  /** Max spans the student may select. When reached, further clicks
   * trigger ``onMaxExceeded`` instead of adding to the selection. */
  maxSelections?: number;
  onMaxExceeded?: () => void;
}): JSX.Element | null {
  const { helper, selectedIndices, onChange, maxSelections, onMaxExceeded } = props;
  const containerRef = useRef<HTMLParagraphElement>(null);

  // Build the HTML once per helper. We do NOT include selectedIndices
  // in the memo — toggling a span would otherwise rebuild the whole
  // <p>, destroy all KaTeX-rendered DOM, and re-render — visible
  // flicker on math-heavy helpers. Selection is applied as a class
  // toggle in a separate imperative effect below.
  const html = useMemo(() => {
    if (!helper?.questionHelper) return '';
    let counter = 0;
    return helper.questionHelper.replace(/\[\[(.*?)\]\]/g, (_, match: string) => {
      const index = counter++;
      return `<div class="digitalSHSATModule__questionHelperHotText" data-index="${index}" data-text="${escapeHtmlAttr(match)}">${match}</div>`;
    });
  }, [helper?.questionHelper]);

  // KaTeX-render the helper once per html change (i.e. per helper
  // text), NOT per selection toggle.
  useEffect(() => {
    if (!containerRef.current) return;
    renderMathInElement(containerRef.current, {
      delimiters: katexDelimiters,
      trust: true,
    });
  }, [html]);

  // Apply the `selected` class to spans in selectedIndices. This is a
  // pure DOM mutation — does not destroy any nodes, preserves KaTeX.
  useEffect(() => {
    if (!containerRef.current) return;
    const spans = containerRef.current.querySelectorAll<HTMLElement>(
      '.digitalSHSATModule__questionHelperHotText',
    );
    spans.forEach((span) => {
      const idx = Number(span.dataset.index);
      if (selectedIndices.includes(idx)) span.classList.add('selected');
      else span.classList.remove('selected');
    });
  }, [selectedIndices, html]);

  if (!helper?.questionHelper) return null;

  const handleClick: React.MouseEventHandler<HTMLElement> = (e) => {
    const target = e.target as HTMLElement;
    if (!target.classList.contains('digitalSHSATModule__questionHelperHotText')) return;
    const idx = Number(target.dataset.index);
    if (!Number.isFinite(idx)) return;
    // Toggling off a selected span is always allowed. Toggling on is
    // capped at ``maxSelections``: when the cap is reached, fire the
    // alert callback and skip the state update so the popup shows but
    // the click doesn't do anything else (matches legacy semantics).
    const alreadySelected = selectedIndices.includes(idx);
    if (!alreadySelected && maxSelections != null && selectedIndices.length >= maxSelections) {
      onMaxExceeded?.();
      return;
    }
    const next = alreadySelected
      ? selectedIndices.filter((i) => i !== idx)
      : [...selectedIndices, idx];
    onChange(next);
  };

  return (
    <div className="digitalSHSATModule__questionContainer">
      <div
        id="digitalSHSATModule__questionHelper"
        className="digitalSHSATModule__questionChoiceWrapper"
      >
        <p
          ref={containerRef}
          className="katex digitalSHSATModule__questionHelperText"
          dangerouslySetInnerHTML={{ __html: html }}
          onClick={handleClick}
        />
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// FillInTheBlank (type 7)
// ---------------------------------------------------------------------------


export type FillInPair = [number, string];


export function FillInTheBlankHelper(props: {
  helper: QuestionHelperPayload | null | undefined;
  values: FillInPair[];
  onChange: (next: FillInPair[]) => void;
  // When true (Math section), restrict the input to a numeric answer and show a popup.
  restrictToMath?: boolean;
}): JSX.Element | null {
  const { helper, values, onChange, restrictToMath } = props;
  const containerRef = useRef<HTMLParagraphElement>(null);
  const [warnMsg, setWarnMsg] = React.useState<string | null>(null);
  const warnTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (warnTimer.current) clearTimeout(warnTimer.current); }, []);

  // Reject invalid input BEFORE it lands in the field, and surface a transient
  // popup explaining the rule. Validates the PROSPECTIVE value (current value
  // with the insertion applied at the caret) so position/count rules — leading
  // minus, single decimal, 7-digit cap — are enforced, not just per-character.
  // Covers typing AND paste (the pasted string arrives as ``data``).
  const handleBeforeInput: React.FormEventHandler<HTMLElement> = (e) => {
    if (!restrictToMath) return;
    const target = e.target as HTMLElement;
    if (!(target instanceof HTMLInputElement)
      || !target.classList.contains('digitalSHSATModule__questionHelperFB')) return;
    const data = (e.nativeEvent as InputEvent).data;
    if (!data) return; // deletions / IME composition / non-text inputs
    const start = target.selectionStart ?? target.value.length;
    const end = target.selectionEnd ?? target.value.length;
    const prospective = target.value.slice(0, start) + data + target.value.slice(end);
    const reason = validateMathEntry(prospective);
    if (reason) {
      e.preventDefault();
      setWarnMsg(reason);
      if (warnTimer.current) clearTimeout(warnTimer.current);
      warnTimer.current = setTimeout(() => setWarnMsg(null), 2600);
    }
  };

  // Build the input HTML once per helper. The values are NOT baked
  // into the html string — committing a value via onBlur would
  // otherwise re-render and recreate every <input>, destroying focus
  // mid-typing in adjacent blanks. Values are written imperatively
  // via .value in a separate effect below.
  const html = useMemo(() => {
    if (!helper?.questionHelper) return '';
    let counter = 0;
    return helper.questionHelper.replace(/\[\[(.*?)\]\]/g, () => {
      const index = counter++;
      return `<input class="digitalSHSATModule__questionHelperFB" data-index="${index}" />`;
    });
  }, [helper?.questionHelper]);

  useEffect(() => {
    if (!containerRef.current) return;
    renderMathInElement(containerRef.current, {
      delimiters: katexDelimiters,
      trust: true,
    });
  }, [html]);

  // Imperatively sync input values from props. Skips the currently
  // focused input so an in-flight keystroke doesn't get clobbered.
  useEffect(() => {
    if (!containerRef.current) return;
    const byIndex: Record<number, string> = {};
    values.forEach(([idx, val]) => { byIndex[idx] = val; });
    const inputs = containerRef.current.querySelectorAll<HTMLInputElement>(
      '.digitalSHSATModule__questionHelperFB',
    );
    inputs.forEach((input) => {
      if (input === document.activeElement) return;
      const idx = Number(input.dataset.index);
      const desired = byIndex[idx] ?? '';
      if (input.value !== desired) input.value = desired;
    });
  }, [values, html]);

  if (!helper?.questionHelper) return null;

  // Commit on every keystroke (``onInput``) AND on blur. Legacy uses
  // blur-only (Module/index.tsx:2349) but that leaves the no-skip
  // submit gate stuck disabled until the student tabs out of the last
  // input — counterintuitive on a multi-blank question where the
  // student types into the last blank and expects the arrow to light
  // up. The imperative-sync effect above already skips the focused
  // input, so committing on input does not clobber in-flight typing.
  const commitValue = (target: HTMLInputElement) => {
    if (!target.classList.contains('digitalSHSATModule__questionHelperFB')) return;
    const idx = Number(target.dataset.index);
    if (!Number.isFinite(idx)) return;
    const next: FillInPair[] = values.filter(([i]) => i !== idx);
    next.push([idx, target.value]);
    next.sort((a, b) => a[0] - b[0]);
    onChange(next);
  };
  const handleInput: React.FormEventHandler<HTMLElement> = (e) => {
    commitValue(e.target as HTMLInputElement);
  };
  const handleBlur: React.FocusEventHandler<HTMLElement> = (e) => {
    commitValue(e.target as HTMLInputElement);
  };

  return (
    <div className="digitalSHSATModule__questionContainer">
      <div className="digitalSHSATModule__questionChoiceWrapper">
        <p
          ref={containerRef}
          className="katex digitalSHSATModule__questionHelperText"
          dangerouslySetInnerHTML={{ __html: html }}
          onBeforeInput={handleBeforeInput}
          onInput={handleInput}
          onBlur={handleBlur}
        />
        {warnMsg && (
          <div className="digitalSHSATModule__inputWarning" role="alert">
            {warnMsg}
          </div>
        )}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// InlineChoice (type 9)
// ---------------------------------------------------------------------------


export type InlineChoicePair = [number, string];


export function InlineChoiceHelper(props: {
  helper: QuestionHelperPayload | null | undefined;
  inlineChoices: Array<{ index: number; options: string[] }>;
  values: InlineChoicePair[];
  onChange: (next: InlineChoicePair[]) => void;
}): JSX.Element | null {
  const { helper, inlineChoices, values, onChange } = props;
  const containerRef = useRef<HTMLParagraphElement>(null);

  const optionsByIndex = useMemo(() => {
    const m: Record<number, string[]> = {};
    for (const ic of inlineChoices) m[ic.index] = ic.options;
    return m;
  }, [inlineChoices]);

  // Build the HTML once per helper. The dropdowns are emitted as plain
  // ``<select>`` elements with ``data-index`` so the change handler can
  // route updates by dropdown position. Values are written imperatively
  // (matches FillInTheBlankHelper) to keep dropdown focus stable.
  //
  // Display vs. value: the option ``value`` keeps the raw stored string
  // so the answer submitted to the server (graded by string-equality
  // against ``QuestionInlineChoiceAnswer.answer``) matches the DB. The
  // visible TEXT is plainized — native ``<option>`` elements cannot
  // render KaTeX (browsers strip markup from option display), so
  // ``$\$90$`` would leak through literally. ``plainizeMath`` strips
  // math to plain text (``$\$90$`` -> ``$90``, ``$9\sqrt{2}$`` -> ``9√2``),
  // see global/plainizeMath.ts.
  const html = useMemo(() => {
    if (!helper?.questionHelper) return '';
    let counter = 0;
    return helper.questionHelper.replace(/\[\[(.*?)\]\]/g, () => {
      const idx = counter++;
      const opts = optionsByIndex[idx] ?? [];
      // "Choose..." placeholder option, matching the legacy inline-choice
      // dropdown (empty value = unanswered).
      const optionsHtml = ['<option value="">Choose...</option>']
        .concat(opts.map((o) =>
          `<option value="${escapeHtmlAttr(o)}">${escapeHtmlAttr(plainizeMath(o))}</option>`,
        ))
        .join('');
      return `<select class="digitalSHSATModule__questionHelperIC" data-index="${idx}">${optionsHtml}</select>`;
    });
  }, [helper?.questionHelper, optionsByIndex]);

  useEffect(() => {
    if (!containerRef.current) return;
    renderMathInElement(containerRef.current, {
      delimiters: katexDelimiters,
      trust: true,
    });
  }, [html]);

  // Sync values imperatively to selects. Skip the active select so an
  // in-flight option pick isn't clobbered mid-keyboard-navigation.
  // Bind native ``change`` listeners on each select too — React's
  // synthetic ``onChange`` doesn't bubble from <select> to a parent
  // <p> consistently across all browsers, and putting onChange on each
  // <select> requires us to find the underlying DOM nodes (they're
  // injected via dangerouslySetInnerHTML, not rendered by React).
  useEffect(() => {
    if (!containerRef.current) return;
    const byIndex: Record<number, string> = {};
    values.forEach(([idx, val]) => { byIndex[idx] = val; });
    const selects = containerRef.current.querySelectorAll<HTMLSelectElement>(
      '.digitalSHSATModule__questionHelperIC',
    );
    const onSelectChange = (e: Event) => {
      const target = e.currentTarget as HTMLSelectElement;
      const idx = Number(target.dataset.index);
      if (!Number.isFinite(idx)) return;
      const next: InlineChoicePair[] = values.filter(([i]) => i !== idx);
      next.push([idx, target.value]);
      next.sort((a, b) => a[0] - b[0]);
      onChange(next);
    };
    selects.forEach((sel) => {
      if (sel !== document.activeElement) {
        const idx = Number(sel.dataset.index);
        const desired = byIndex[idx] ?? '';
        if (sel.value !== desired) sel.value = desired;
      }
      sel.addEventListener('change', onSelectChange);
    });
    return () => {
      selects.forEach((sel) => sel.removeEventListener('change', onSelectChange));
    };
  }, [values, html, onChange]);

  if (!helper?.questionHelper) return null;

  return (
    <div className="digitalSHSATModule__questionContainer">
      <div className="digitalSHSATModule__questionChoiceWrapper">
        <p
          ref={containerRef}
          className="katex digitalSHSATModule__questionHelperText"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Shared
// ---------------------------------------------------------------------------


const katexDelimiters = [
  { left: '$$', right: '$$', display: true },
  { left: '$', right: '$', display: false },
  { left: '\\(', right: '\\)', display: false },
  { left: '\\[', right: '\\]', display: true },
];


/**
 * Attaches mouse + touch drag handlers to all elements inside
 * ``containerRef`` matching ``itemSelector`` so they can be dragged
 * onto elements matching ``dropZoneSelector``. On release over a
 * zone, ``onDrop(itemId, zoneId)`` fires. Used by D&D Bucket + Blank.
 *
 * Ported (loosely) from ``Module/DragAndDropBucket/index.tsx:dragMove``.
 * Coexists with click-to-place: clicking the item still works via the
 * separate ``onClick`` handlers in the helpers, so users on touchscreens
 * or who prefer tap-tap can still complete the question.
 */
function useDndDragAttach(params: {
  containerRef: React.RefObject<HTMLElement>;
  itemSelector: string;
  dropZoneSelector: string;
  getItemId: (el: HTMLElement) => number | null;
  getZoneId: (el: HTMLElement) => number | null;
  onDrop: (itemId: number, zoneId: number) => void;
  // Re-attach trigger (items can re-mount across state changes).
  deps?: ReadonlyArray<unknown>;
}): void {
  const { containerRef, itemSelector, dropZoneSelector, getItemId, getZoneId, onDrop } = params;
  const deps = params.deps ?? [];
  useEffect(() => {
    if (!containerRef.current) return;
    const container = containerRef.current;
    const items = Array.from(container.querySelectorAll<HTMLElement>(itemSelector));
    const cleanups: Array<() => void> = [];

    items.forEach((item) => {
      const onDown = (e: MouseEvent | TouchEvent) => {
        const itemId = getItemId(item);
        if (itemId == null) return;
        // IMPORTANT: do NOT preventDefault or float the item on mousedown.
        // Doing so turned every plain CLICK into a zero-distance "drag",
        // which swallowed the React onClick that selects the item — so
        // click-to-place never worked. Instead we wait for real movement
        // past a small threshold before starting a drag; a click that never
        // crosses the threshold falls through to onClick untouched.
        const rect = item.getBoundingClientRect();
        const startX = e.type === 'mousedown'
          ? (e as MouseEvent).clientX
          : (e as TouchEvent).touches[0].clientX;
        const startY = e.type === 'mousedown'
          ? (e as MouseEvent).clientY
          : (e as TouchEvent).touches[0].clientY;
        const offsetX = startX - rect.left;
        const offsetY = startY - rect.top;
        const prevStyle = item.getAttribute('style') ?? '';
        const DRAG_THRESHOLD = 5; // px before a press becomes a drag
        let dragging = false;
        let lastX = startX;
        let lastY = startY;

        const startDrag = () => {
          dragging = true;
          // Float the item under the cursor. ``pointer-events: none`` so
          // hit-tests during the drag fall through to the drop zones.
          item.style.position = 'fixed';
          item.style.zIndex = '1000';
          item.style.width = `${rect.width}px`;
          item.style.height = `${rect.height}px`;
          item.style.pointerEvents = 'none';
          item.style.left = `${rect.left}px`;
          item.style.top = `${rect.top}px`;
          item.style.opacity = '0.85';
        };

        const highlightHovered = (x: number, y: number) => {
          const zones = container.querySelectorAll<HTMLElement>(dropZoneSelector);
          zones.forEach((zone) => {
            const zr = zone.getBoundingClientRect();
            const inside = x >= zr.left && x <= zr.right && y >= zr.top && y <= zr.bottom;
            zone.classList.toggle('digitalSHSATModule__dndDropHover', inside);
          });
        };

        const move = (ev: Event) => {
          let x: number; let y: number;
          if (ev.type === 'mousemove') {
            const me = ev as MouseEvent;
            x = me.clientX; y = me.clientY;
          } else {
            const te = ev as TouchEvent;
            x = te.touches[0].clientX; y = te.touches[0].clientY;
          }
          lastX = x; lastY = y;
          if (!dragging) {
            if (Math.abs(x - startX) < DRAG_THRESHOLD && Math.abs(y - startY) < DRAG_THRESHOLD) {
              return; // still a click, not a drag
            }
            startDrag();
          }
          ev.preventDefault();
          item.style.left = `${x - offsetX}px`;
          item.style.top = `${y - offsetY}px`;
          highlightHovered(x, y);
        };

        const cleanup = () => {
          document.removeEventListener('mousemove', move);
          document.removeEventListener('mouseup', release);
          document.removeEventListener('touchmove', move);
          document.removeEventListener('touchend', release);
        };

        const release = (ev: Event) => {
          // Never dragged past the threshold → it was a click. Leave it for
          // the React onClick (select / place) and do nothing here.
          if (!dragging) {
            cleanup();
            return;
          }
          let x: number; let y: number;
          if (ev.type === 'mouseup') {
            const me = ev as MouseEvent;
            x = me.clientX; y = me.clientY;
          } else {
            const te = ev as TouchEvent;
            const t = te.changedTouches[0] ?? te.touches[0];
            x = t?.clientX ?? lastX;
            y = t?.clientY ?? lastY;
          }
          item.setAttribute('style', prevStyle);
          container.querySelectorAll<HTMLElement>(dropZoneSelector).forEach((z) => {
            z.classList.remove('digitalSHSATModule__dndDropHover');
          });
          const zones = Array.from(container.querySelectorAll<HTMLElement>(dropZoneSelector));
          for (const zone of zones) {
            const zr = zone.getBoundingClientRect();
            if (x >= zr.left && x <= zr.right && y >= zr.top && y <= zr.bottom) {
              const zoneId = getZoneId(zone);
              if (zoneId != null) {
                onDrop(itemId, zoneId);
                break;
              }
            }
          }
          cleanup();
        };

        document.addEventListener('mousemove', move);
        document.addEventListener('mouseup', release);
        document.addEventListener('touchmove', move, { passive: false });
        document.addEventListener('touchend', release);
      };

      item.addEventListener('mousedown', onDown);
      item.addEventListener('touchstart', onDown, { passive: false });
      cleanups.push(() => {
        item.removeEventListener('mousedown', onDown);
        item.removeEventListener('touchstart', onDown);
      });
    });

    return () => cleanups.forEach((fn) => fn());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- caller controls deps
  }, [containerRef, itemSelector, dropZoneSelector, getItemId, getZoneId, onDrop, ...deps]);
}


// ---------------------------------------------------------------------------
// DragAndDropBucket (type 5)
// ---------------------------------------------------------------------------


export interface BucketPayload {
  bucketId: number;
  index: number;
  name: string | null;
  maxItems: number | null;
}

export interface DnDItem {
  id: number;
  index: number;
  answer: string;
}

/** Submit payload entry: ``[bucketId, itemId]``. */
export type BucketPair = [number, number];


/**
 * Click-to-place D&D Buckets. Adaptive variant of the legacy drag-based
 * widget. Click an item to select it, then click a bucket to drop it.
 * Click an item already in a bucket to lift it (re-select it) or use the
 * "Return to tray" button to unbucket.
 *
 * The drag layer from legacy is intentionally NOT ported — the adaptive
 * smoke test exercises payload shape + grading, not pointer-physics.
 * Drag can be re-added later without changing the submit contract.
 */
export function DragAndDropBucketHelper(props: {
  buckets: BucketPayload[];
  items: DnDItem[];
  selected: BucketPair[];
  onChange: (next: BucketPair[]) => void;
}): JSX.Element {
  const { buckets, items, selected, onChange } = props;
  const [selectedItem, setSelectedItem] = React.useState<number | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  // KaTeX-render whenever items render or move. Required because item
  // text can contain math delimiters (e.g. ``$\\$`` line-break markers
  // baked into legacy SHSAT D&D content) and React re-mounts the item
  // DOM nodes whenever an item moves between tray and bucket — so the
  // parent's one-shot ``[question?.id]`` effect doesn't cover those
  // re-renders. ``renderMathInElement`` is idempotent on already-
  // rendered nodes (KaTeX output has no more raw delimiters to match).
  useEffect(() => {
    if (!rootRef.current) return;
    const katexEls = rootRef.current.getElementsByClassName('katex');
    for (let i = 0; i < katexEls.length; i++) {
      renderMathInElement(katexEls[i] as HTMLElement, {
        delimiters: katexDelimiters,
        trust: true,
      });
    }
  }, [items, selected]);

  const isItemBucketed = (itemId: number) =>
    selected.some(([, id]) => id === itemId);

  const itemBucketId = (itemId: number) => {
    const entry = selected.find(([, id]) => id === itemId);
    return entry ? entry[0] : null;
  };

  // Shared placement logic used by both click-to-place and drag-and-drop
  // paths. The drag path supplies ``itemId`` explicitly; the click path
  // falls back to ``selectedItem`` state.
  const placeItemInBucket = (itemId: number, bucketId: number) => {
    const bucket = buckets.find((b) => b.bucketId === bucketId);
    if (!bucket) return;
    const cap = bucket.maxItems ?? Math.max(items.length - 1, 1);
    const already = selected.filter(([b]) => b === bucketId).length;
    const wasInThisBucket = itemBucketId(itemId) === bucketId;
    if (!wasInThisBucket && already >= cap) {
      // Bucket full — silently no-op (matches legacy disabled state).
      return;
    }
    const next: BucketPair[] = selected.filter(([, id]) => id !== itemId);
    next.push([bucketId, itemId]);
    onChange(next);
    setSelectedItem(null);
  };

  const placeInBucket = (bucketId: number) => {
    if (selectedItem == null) return;
    placeItemInBucket(selectedItem, bucketId);
  };

  const removeFromBucket = (itemId: number) => {
    onChange(selected.filter(([, id]) => id !== itemId));
    if (selectedItem === itemId) setSelectedItem(null);
  };

  // Drag-and-drop wiring. Items are draggable; buckets are drop zones.
  // Coexists with click-to-place — both flows write the same answer
  // state via ``placeItemInBucket``. Re-attaches whenever items / buckets
  // re-mount across selection state changes.
  useDndDragAttach({
    containerRef: rootRef,
    itemSelector: '.digitalSHSATModule__bucketItem[data-item-id]',
    dropZoneSelector: '.digitalSHSATModule__bucket[data-bucket-id]',
    getItemId: (el) => {
      const v = el.getAttribute('data-item-id');
      return v != null ? Number(v) : null;
    },
    getZoneId: (el) => {
      const v = el.getAttribute('data-bucket-id');
      return v != null ? Number(v) : null;
    },
    onDrop: (itemId, bucketId) => placeItemInBucket(itemId, bucketId),
    deps: [items, selected, buckets],
  });

  return (
    <div className="digitalSHSATModule__questionContainer" ref={rootRef}>
      <div id="digitalSHSATModule__bucketItems" className="digitalSHSATModule__bucketItems">
        {items.filter((item) => !isItemBucketed(item.id)).map((item) => (
          // Single in-flow item (no absolute-overlay sizer). The legacy pattern
          // measured a hidden sizer at a slightly different width than the
          // absolutely-positioned visible item, so multi-line choices wrapped to
          // one extra line and spilled onto the next grid row. Flowing the real
          // box lets the grid cell size to its content — no overlap.
          <div
            key={item.id}
            className={selectedItem === item.id
              ? 'digitalSHSATModule__bucketItem digitalSHSATModule__bucketItem--adaptive digitalSHSATModule__bucketItem--selected'
              : 'digitalSHSATModule__bucketItem digitalSHSATModule__bucketItem--adaptive'}
            data-item-id={item.id}
            onClick={() => setSelectedItem(item.id)}
            role="button"
            tabIndex={0}
            style={{ cursor: 'grab' }}
          >
            <p className="katex">{item.answer}</p>
          </div>
        ))}
      </div>
      <div className="digitalSHSATModule__buckets">
        {buckets.map((bucket) => (
          <div
            key={bucket.bucketId}
            data-bucket-id={bucket.bucketId}
            className={selectedItem != null
              ? 'digitalSHSATModule__bucket digitalSHSATModule__bucketDestination'
              : 'digitalSHSATModule__bucket'}
            onClick={() => placeInBucket(bucket.bucketId)}
          >
            <h4 className="katex digitalSHSATModule__bucketName">{bucket.name ?? ''}</h4>
            {items.filter((item) => itemBucketId(item.id) === bucket.bucketId).map((item) => (
              <div
                key={`${bucket.bucketId}-${item.id}`}
                className={selectedItem === item.id
                  ? 'digitalSHSATModule__bucketItem digitalSHSATModule__bucketItem--adaptive digitalSHSATModule__bucketItem--selected'
                  : 'digitalSHSATModule__bucketItem digitalSHSATModule__bucketItem--adaptive'}
                data-item-id={item.id}
                onClick={(e) => {
                  e.stopPropagation();
                  removeFromBucket(item.id);
                }}
                style={{ cursor: 'grab' }}
              >
                <p className="katex">{item.answer}</p>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// DragAndDropBlank (type 6)
// ---------------------------------------------------------------------------


/** Submit payload entry: ``[itemId, blankIndex]``. */
export type BlankPair = [number, number];


/**
 * Click-to-place D&D Blanks. Helper text contains ``[[N]]`` markers
 * that become drop targets. Click an item, click a blank to place it,
 * click a filled blank to lift the item back to the tray.
 *
 * Like DragAndDropBucketHelper, the drag layer is intentionally
 * deferred — click-to-place is sufficient for end-to-end smoke flow.
 */
export function DragAndDropBlankHelper(props: {
  helper: QuestionHelperPayload | null | undefined;
  items: DnDItem[];
  selected: BlankPair[];
  onChange: (next: BlankPair[]) => void;
}): JSX.Element | null {
  const { helper, items, selected, onChange } = props;
  const containerRef = useRef<HTMLParagraphElement>(null);
  const trayRef = useRef<HTMLDivElement>(null);
  // Wraps both the tray and the helper text so the drag hook can
  // resolve items + drop blanks via a single query.
  const rootRef = useRef<HTMLDivElement>(null);
  const [selectedItem, setSelectedItem] = React.useState<number | null>(null);

  const itemForBlank = (blankIndex: number) => {
    const entry = selected.find(([, idx]) => idx === blankIndex);
    return entry ? entry[0] : null;
  };

  const isItemPlaced = (itemId: number) =>
    selected.some(([id]) => id === itemId);

  const placeItemInBlank = (itemId: number, blankIndex: number) => {
    const next: BlankPair[] = selected
      .filter(([id, idx]) => id !== itemId && idx !== blankIndex);
    next.push([itemId, blankIndex]);
    onChange(next);
    setSelectedItem(null);
  };

  const placeInBlank = (blankIndex: number) => {
    if (selectedItem == null) return;
    placeItemInBlank(selectedItem, blankIndex);
  };

  const removeFromBlank = (blankIndex: number) => {
    onChange(selected.filter(([, idx]) => idx !== blankIndex));
  };

  // Build the helper HTML once per helper. Blanks are emitted as inline
  // ``<span>`` boxes with ``data-index``; the click handler routes by
  // the dataset attribute. Filled vs. empty styling is applied
  // imperatively on each render of ``selected`` so KaTeX nodes survive.
  const html = useMemo(() => {
    if (!helper?.questionHelper) return '';
    // 1-based blank index to match the LEGACY flow and the DB
    // ``QuestionDragAndDropBlankAnswer.correctIndex`` (seeded 1-based by
    // both ingest scripts). The grader compares ``[itemId, blankIndex]``
    // pairs raw with no normalization, so a 0-based index here mis-grades
    // every type-6 question. ``data-index`` is the only source for the
    // saved pair AND the placed-item read-back, so they stay consistent.
    let counter = 1;
    return helper.questionHelper.replace(/\[\[(.*?)\]\]/g, () => {
      const idx = counter++;
      return `<span class="digitalSHSATModule__blankQuestionHelperBox" data-index="${idx}"></span>`;
    });
  }, [helper?.questionHelper]);

  useEffect(() => {
    if (!containerRef.current) return;
    renderMathInElement(containerRef.current, {
      delimiters: katexDelimiters,
      trust: true,
    });
  }, [html]);

  // Drag-and-drop wiring. Tray items are the pill chips
  // (``.digitalSHSATModule__blankItem``, distinct from the larger
  // ``.bucketItem``); the ``.blankQuestionHelperBox`` spans inside the
  // helper text are the drop zones. Both live inside ``rootRef``.
  useDndDragAttach({
    containerRef: rootRef,
    itemSelector: '.digitalSHSATModule__blankItem[data-item-id]',
    dropZoneSelector: '.digitalSHSATModule__blankQuestionHelperBox[data-index]',
    getItemId: (el) => {
      const v = el.getAttribute('data-item-id');
      return v != null ? Number(v) : null;
    },
    getZoneId: (el) => {
      const v = el.getAttribute('data-index');
      return v != null ? Number(v) : null;
    },
    onDrop: (itemId, blankIndex) => placeItemInBlank(itemId, blankIndex),
    deps: [items, selected, html],
  });

  // KaTeX-render tray items. Items can contain math delimiters
  // (legacy SHSAT line-break markers) — without this, ``$\\$ ...``
  // shows literally. Same idempotent-on-re-render pattern as
  // DragAndDropBucketHelper.
  useEffect(() => {
    if (!trayRef.current) return;
    const katexEls = trayRef.current.getElementsByClassName('katex');
    for (let i = 0; i < katexEls.length; i++) {
      renderMathInElement(katexEls[i] as HTMLElement, {
        delimiters: katexDelimiters,
        trust: true,
      });
    }
  }, [items, selected]);

  // Paint the current ``selected`` state into the blanks. Pure DOM
  // mutation — preserves KaTeX-rendered surrounding text.
  useEffect(() => {
    if (!containerRef.current) return;
    const blanks = containerRef.current.querySelectorAll<HTMLElement>(
      '.digitalSHSATModule__blankQuestionHelperBox',
    );
    blanks.forEach((blank) => {
      const idx = Number(blank.dataset.index);
      const itemId = itemForBlank(idx);
      if (itemId != null) {
        const item = items.find((i) => i.id === itemId);
        // Render the placed item INLINE (no nested ``blankItem``
        // wrapper — that class has ``position: absolute`` from legacy
        // CSS which would escape positioning when the parent has no
        // ``position: relative``). The ``.filled`` modifier on the
        // box applies the chip styling directly.
        const safeAnswer = escapeHtmlAttr(item?.answer ?? '');
        blank.innerHTML = `<span class="katex" data-filled-item-id="${itemId}">${safeAnswer}</span>`;
        blank.classList.add('filled');
        renderMathInElement(blank, {
          delimiters: katexDelimiters,
          trust: true,
        });
      } else {
        blank.textContent = '';
        blank.classList.remove('filled');
      }
    });
  }, [selected, html, items]);

  if (!helper?.questionHelper) return null;

  const onHelperClick: React.MouseEventHandler<HTMLElement> = (e) => {
    const target = e.target as HTMLElement;
    if (!target.classList.contains('digitalSHSATModule__blankQuestionHelperBox')) return;
    const idx = Number(target.dataset.index);
    if (!Number.isFinite(idx)) return;
    const placedItemId = itemForBlank(idx);
    if (placedItemId != null) {
      // Legacy behavior: clicking a filled box lifts the word back to the
      // tray AND selects it, so the now-empty box shows the dashed-blue
      // placeholder (the destination highlight) and the word can be
      // re-placed in another box or dropped back by clicking the tray.
      removeFromBlank(idx);
      setSelectedItem(placedItemId);
      return;
    }
    if (selectedItem != null) {
      placeInBlank(idx);
    }
  };

  return (
    <div className="digitalSHSATModule__questionContainer" ref={rootRef}>
      {/* Tray uses ``blankItems`` (not ``bucketItems``) — pill-style
          chips with welledBlue border, separate from the larger bucket
          chip styles. Legacy renders the destination-bg variant when an
          item is in flight; mirror that with a state-driven modifier. */}
      <div
        className={selectedItem != null
          ? 'digitalSHSATModule__blankItems digitalSHSATModule__blankItemsDestination'
          : 'digitalSHSATModule__blankItems'}
        ref={trayRef}
        onClick={selectedItem != null
          // Clicking back into the tray (away from a blank) returns the
          // selected item to its tray slot. Matches the legacy
          // ``handleBlankEmpty`` behaviour without arguments.
          ? () => {
              if (selectedItem == null) return;
              onChange(selected.filter(([id]) => id !== selectedItem));
              setSelectedItem(null);
            }
          : undefined}
      >
        {items.filter((item) => !isItemPlaced(item.id)).map((item) => (
          <div
            key={item.id}
            className={selectedItem === item.id
              ? 'digitalSHSATModule__blankItemBackground digitalSHSATModule__blankItemBackground--selected'
              : 'digitalSHSATModule__blankItemBackground'}
          >
            <div
              className="digitalSHSATModule__blankItem"
              data-item-id={item.id}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedItem(item.id);
              }}
              role="button"
              tabIndex={0}
              style={{ cursor: 'grab' }}
            >
              <p className="katex">{item.answer}</p>
            </div>
            <p className="katex">{item.answer}</p>
          </div>
        ))}
      </div>
      <div className="digitalSHSATModule__questionChoiceWrapper">
        {/* The ``...Destination`` modifier (added while an item is selected)
            is what the legacy CSS keys off to give the empty boxes their
            dashed-blue highlight + hover — so the student sees where the
            selected word can go. Without this wrapper class the boxes stayed
            plain. */}
        <div
          className={selectedItem != null
            ? 'digitalSHSATModule__blankQuestionHelper digitalSHSATModule__blankQuestionHelperDestination'
            : 'digitalSHSATModule__blankQuestionHelper'}
        >
          <p
            ref={containerRef}
            className="katex digitalSHSATModule__blankQuestionHelperText"
            onClick={onHelperClick}
            dangerouslySetInnerHTML={{ __html: html }}
          />
        </div>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// TableGrid (type 8)
// ---------------------------------------------------------------------------


export interface TableGridCell {
  id: number;
  index: number;
  name: string;
}

/** Submit payload entry: ``[rowId, columnId]``. */
export type TableGridPair = [number, number];


/**
 * Table Grid widget — each cell is an independent toggle. Mirrors
 * legacy semantics at ``Module/TableGrid/index.tsx``: clicking any
 * cell toggles ``[rowId, colId]`` in/out of the answer array. Multiple
 * cells per row are allowed (SHSAT table-grid questions vary; some
 * are one-per-row, others not — the grader compares against the full
 * set of correct (row, col) pairs).
 *
 * A11Y NOTE: checkbox-style inputs match the legacy DOM, which lets
 * the same CSS module ``.../Module/TableGrid/index.css`` apply. The
 * single-row-radio variant would be more accessible for one-per-row
 * questions but would diverge from legacy. Tracked as follow-up.
 */
export function TableGridHelper(props: {
  rows: TableGridCell[];
  columns: TableGridCell[];
  selected: TableGridPair[];
  onChange: (next: TableGridPair[]) => void;
}): JSX.Element {
  const { rows, columns, selected, onChange } = props;
  const rootRef = useRef<HTMLDivElement>(null);

  const isSelected = (rowId: number, colId: number) =>
    selected.some(([r, c]) => r === rowId && c === colId);

  const toggle = (rowId: number, colId: number) => {
    if (isSelected(rowId, colId)) {
      onChange(selected.filter(([r, c]) => !(r === rowId && c === colId)));
      return;
    }
    onChange([...selected, [rowId, colId]]);
  };

  // KaTeX-render row/column header text once per row+column change.
  useEffect(() => {
    if (!rootRef.current) return;
    const katexEls = rootRef.current.getElementsByClassName('katex');
    for (let i = 0; i < katexEls.length; i++) {
      renderMathInElement(katexEls[i] as HTMLElement, {
        delimiters: katexDelimiters,
        trust: true,
      });
    }
  }, [rows, columns]);

  // ``digitalSHSATModule__questionContainer`` has ``width: fit-content``
  // in the shared legacy CSS, which would let the table push the
  // wrapper past the question column. Override inline so the wrapper
  // stays bound to the column width and the inner div can actually
  // overflow-scroll. Same reason for ``minWidth: 0``.
  return (
    <div
      className="digitalSHSATModule__questionContainer"
      ref={rootRef}
      style={{ width: '100%', maxWidth: '100%', minWidth: 0 }}
    >
      {/* Horizontal scroll wrapper. The scrollbar inherits legacy's
          ``::-webkit-scrollbar`` width/thumb styling defined inside
          ``.digitalSHSATModule`` (Module/index.css:49) so it looks
          consistent with the body's vertical scrollbar — except
          legacy only specifies ``width: 8px`` (vertical-only); we add
          ``height: 8px`` here so the horizontal bar is the same
          thickness, via ``className`` -> styled rule below. */}
      <div className="digitalSHSATModule__tableGridScroll" style={{ width: '100%', overflowX: 'auto' }}>
      <table className="digitalSHSATModule__tableGrid">
        <tbody>
          <tr>
            <th />
            {columns.map((col) => (
              <th key={`c${col.id}`}>
                <p className="katex">{col.name}</p>
              </th>
            ))}
          </tr>
          {rows.map((row) => (
            <tr key={`r${row.id}`}>
              <th>
                <p className="katex">{row.name}</p>
              </th>
              {columns.map((col) => (
                <td key={`r${row.id}c${col.id}`}>
                  <input
                    type="checkbox"
                    checked={isSelected(row.id, col.id)}
                    onChange={() => toggle(row.id, col.id)}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Equation Editor (types 11 + 12)
// ---------------------------------------------------------------------------


/**
 * MathLive ``<math-field>`` web component wrapper. Type 11 = LaTeX
 * answer; type 12 = numeric answer (same component, server graders
 * are different).
 *
 * The hosting page must ``import 'mathlive'`` once before the field
 * mounts — it registers the custom element. AdaptiveModule.tsx does
 * the import at the top.
 */
export function EquationEditorHelper(props: {
  value: string;
  onChange: (next: string) => void;
  questionType?: number;
}): JSX.Element {
  const { value, onChange, questionType = 11 } = props;
  const ref = useRef<HTMLElement>(null);

  // Push value into the field whenever the parent state changes from
  // somewhere other than this field — same imperative pattern as
  // FillInTheBlank/InlineChoice for stable focus.
  useEffect(() => {
    const el = ref.current as unknown as { value?: string } | null;
    if (!el) return;
    if (el.value !== value) el.value = value;
  }, [value]);

  // Configure the SHSAT custom math keyboard (layout, input restriction,
  // tooltips, toolbar) — same setup legacy runs. Mounts per equation slot;
  // the returned cleanup tears it down AND hides the global keyboard so it
  // doesn't leak onto the next (non-equation) slot. ``onChange('')`` backs
  // the toolbar's delete-all button.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const teardown = setupShsatMathKeyboard(el, questionType, () => onChange(''));
    return teardown;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionType]);

  // No JSX children — MathLive ignores children after hydration, and
  // passing ``{value}`` here would fight the imperative sync above
  // when restoring prior state (initial children win on mount; effect
  // reconciles after first flush, briefly showing stale content). Keep
  // the field uncontrolled at the DOM level and drive it via the ref.
  return (
    <div className="digitalSHSATModule__questionContainer">
      <div className="digitalSHSATModule__eeContainer">
        <math-field
          ref={ref as React.RefObject<HTMLElement>}
          className="digitalSHSATModule__eeInput"
          virtual-keyboard-mode="manual"
          style={{ width: '620px', height: '50px', fontSize: '20px' }}
          onInput={(e: React.SyntheticEvent<HTMLElement>) => {
            const next = (e.target as unknown as { value?: string }).value ?? '';
            onChange(next);
          }}
        />
      </div>
    </div>
  );
}
